import { type FC, useState, useEffect, useMemo } from 'react';
import { useSelector } from 'react-redux';
import { Tab, Nav } from 'react-bootstrap';

import { type RootState } from '@editor2/reducers/root';
import { tocPage } from './styles';
import {
	checkUnsavedTocChanges,
	checkUnsavedConfigChanges,
	checkUnsavedFileChanges
} from '@editor2/services/unsavedChangesService';

import EditorForm from './components/EditorForm';
import TableOfContents from './components/TableOfContents';
import YamlEditor from '@editor2/containers/TableOfContentsPage/components/YamlEditor';
import Tools from './components/Tools';
import Files from './components/Files';

import Header from '@editor2/components/Header';
import UnsavedChangesTooltip from '@editor2/components/UnsavedChangesTooltip';
import { useConnectSocket } from '@editor2/hooks/useConnectSocket';
import { useTabTitle } from '@editor2/hooks/useTabTitle';
import { sanitizeCourseId } from '@editor2/services/helpersService';
import { useSettingsMode } from '@editor2/hooks/useSettingsMode';
import SideOptionsPanel from '../App/SideOptionsPanel';

interface Props {
	match: {
		params: {
			courseId: string;
		};
	};
}

const TableOfContentsPage: FC<Props> = (props) => {
	const { match } = props;
	const { courseId: courseIdParam } = match.params;
	const courseId = sanitizeCourseId(courseIdParam);

	const { chapters, course, changes, settingsMode, book, policies, activeFileUploads } =
		useSelector((state: RootState) => state.courses[courseId]);

	const [key, setKey] = useState('toc');

	useConnectSocket({ roomName: 'toc', courseId });
	useTabTitle({ title: `${course.name}` });

	useEffect(() => {
		if (checkUnsavedConfigChanges(changes)) setKey('settings');
		if (key === 'settings' && checkUnsavedTocChanges(changes) && !settingsMode) setKey('toc');
	}, [changes]);

	useSettingsMode({ settingsMode, setKey });

	const { hasUnsavedTocChanges, hasUnsavedConfigChanges, hasUnsavedFileChanges } = useMemo(
		() => ({
			hasUnsavedTocChanges: checkUnsavedTocChanges(changes),
			hasUnsavedConfigChanges: checkUnsavedConfigChanges(changes),
			hasUnsavedFileChanges: checkUnsavedFileChanges(changes) || (activeFileUploads ?? 0) > 0
		}),
		[changes, activeFileUploads]
	);

	return (
		<>
			{book && (
				<div className={tocPage}>
					<Header course={course} />
					<SideOptionsPanel courseId={courseId} />

					<Tab.Container id="" activeKey={key} onSelect={(k) => setKey(k)} unmountOnExit={true}>
						<Nav variant="tabs" className="tocTabs">
							<UnsavedChangesTooltip
								condition={hasUnsavedConfigChanges || settingsMode || hasUnsavedFileChanges}>
								<Nav.Item>
									<Nav.Link
										eventKey="course"
										disabled={hasUnsavedConfigChanges || settingsMode || hasUnsavedFileChanges}>
										Properties
									</Nav.Link>
								</Nav.Item>
							</UnsavedChangesTooltip>

							<UnsavedChangesTooltip
								condition={hasUnsavedConfigChanges || settingsMode || hasUnsavedFileChanges}>
								<Nav.Item>
									<Nav.Link
										eventKey="toc"
										disabled={hasUnsavedConfigChanges || settingsMode || hasUnsavedFileChanges}>
										Contents
									</Nav.Link>
								</Nav.Item>
							</UnsavedChangesTooltip>

							<UnsavedChangesTooltip
								condition={(hasUnsavedTocChanges && !settingsMode) || hasUnsavedFileChanges}>
								<Nav.Item>
									<Nav.Link
										eventKey="settings"
										disabled={(hasUnsavedTocChanges && !settingsMode) || hasUnsavedFileChanges}>
										Settings
									</Nav.Link>
								</Nav.Item>
							</UnsavedChangesTooltip>

							<UnsavedChangesTooltip
								condition={
									hasUnsavedTocChanges ||
									hasUnsavedConfigChanges ||
									settingsMode ||
									hasUnsavedFileChanges
								}>
								<Nav.Item>
									<Nav.Link
										eventKey="tools"
										disabled={
											hasUnsavedTocChanges ||
											hasUnsavedConfigChanges ||
											settingsMode ||
											hasUnsavedFileChanges
										}>
										Tools
									</Nav.Link>
								</Nav.Item>
							</UnsavedChangesTooltip>

							<UnsavedChangesTooltip
								condition={hasUnsavedTocChanges || hasUnsavedConfigChanges || settingsMode}>
								<Nav.Item>
									<Nav.Link
										eventKey="files"
										disabled={hasUnsavedTocChanges || hasUnsavedConfigChanges || settingsMode}>
										Files
									</Nav.Link>
								</Nav.Item>
							</UnsavedChangesTooltip>
						</Nav>

						<Tab.Content>
							<Tab.Pane eventKey="course">
								<EditorForm book={book} courseId={courseId} />
							</Tab.Pane>

							<Tab.Pane eventKey="toc">
								<TableOfContents book={book} chapters={chapters} courseId={courseId} />
							</Tab.Pane>

							<Tab.Pane eventKey="settings">
								<YamlEditor
									yamlConfig={book.yaml_config}
									canEditResourceConfig={policies.canEditResourceConfig}
									courseId={courseId}
								/>
							</Tab.Pane>

							<Tab.Pane eventKey="tools">
								<Tools courseId={courseId} />
							</Tab.Pane>

							<Tab.Pane eventKey="files">
								<Files courseId={courseId} />
							</Tab.Pane>
						</Tab.Content>
					</Tab.Container>
				</div>
			)}
		</>
	);
};

export default TableOfContentsPage;
